import {ReactElement, ReactNode, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ROUTE__EVENT_DETAILS} from '@shared/constants';
import {useGetEvent} from '../../apiHooks';

const EMPTY_VALUE = '—';
const EVENT_PREVIEW_STALE_TIME = 5 * 60 * 1000;

const WhiteTooltipArrow = styled('span')({
  overflow: 'hidden',
  position: 'absolute',
  width: '1em',
  height: '0.71em',
  boxSizing: 'border-box',
  color: '#fff',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: '100%',
    height: '100%',
    backgroundColor: 'currentColor',
    transform: 'rotate(45deg)',
  },
});

interface EventHoverCardProps {
  eventId: string;
  children: ReactElement;
}

interface EventLinkProps {
  eventId: string;
}

const PreviewField = ({
  label,
  value,
  action,
}: {
  label: string;
  value: ReactNode;
  action?: ReactNode;
}) => (
  <>
    <Typography variant="b2regular" color="text.secondary">
      {label}
    </Typography>
    <Box display="flex" minWidth={0} alignItems="center">
      <Typography variant="b2regular" noWrap sx={{minWidth: 0}}>
        {value}
      </Typography>
      {action}
    </Box>
  </>
);

const EventHoverCardContent = ({
  eventId,
}: Pick<EventHoverCardProps, 'eventId'>) => {
  const {
    data: event,
    isLoading,
    isError,
  } = useGetEvent(eventId, {
    enabled: Boolean(eventId),
    staleTime: EVENT_PREVIEW_STALE_TIME,
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        minWidth={320}
        minHeight={160}
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError || !event) {
    return (
      <Box minWidth={320} p={2}>
        <Typography variant="b2regular">Unable to load event data</Typography>
      </Box>
    );
  }

  const subCategories = event.subCategories.map(({name}) => name).join(', ');

  return (
    <Box width={520} maxWidth="calc(100vw - 32px)" px={2} pt={2.5} pb={2}>
      <Box display="flex" gap={2} alignItems="flex-start">
        <Avatar
          alt={event.name}
          src={event.logoUrl || undefined}
          variant="rounded"
          sx={{width: 56, height: 56}}
        >
          {event.name.slice(0, 1)}
        </Avatar>

        <Box
          display="grid"
          flex={1}
          gridTemplateColumns="120px minmax(0, 1fr)"
          alignItems="center"
          columnGap={1}
          rowGap={0.5}
        >
          <PreviewField
            label="Event ID"
            value={event.eventId}
            action={
              <Box ml="auto">
                <Tooltip title="Open event">
                  <IconButton
                    aria-label="Open event"
                    component={RouterLink}
                    rel="noopener noreferrer"
                    size="small"
                    target="_blank"
                    to={ROUTE__EVENT_DETAILS.replace(
                      ':event_id',
                      event.eventId,
                    )}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          />
          <PreviewField
            label="Short name"
            value={event.shortName || EMPTY_VALUE}
          />
          <PreviewField label="Name" value={event.name || EMPTY_VALUE} />
          <PreviewField
            label="Category"
            value={event.category.name || EMPTY_VALUE}
          />
          <PreviewField
            label="Subcategory"
            value={subCategories || EMPTY_VALUE}
          />
          <PreviewField
            label="Topic"
            value={event.topic?.name || EMPTY_VALUE}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const EventHoverCard = ({eventId, children}: EventHoverCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip
      arrow
      disableFocusListener
      placement="right-start"
      open={isOpen}
      slots={{arrow: WhiteTooltipArrow}}
      slotProps={{
        popper: {
          modifiers: [{name: 'arrow', options: {padding: 26}}],
          sx: {
            '& .MuiTooltip-arrow': {
              filter: 'drop-shadow(-1px 2px 2px rgb(0 0 0 / 20%))',
            },
          },
        },
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: 1,
            borderColor: 'divider',
            boxShadow: 4,
            color: 'text.primary',
            maxWidth: 'none',
            p: 0,
          },
        },
      }}
      title={isOpen ? <EventHoverCardContent eventId={eventId} /> : ''}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
    >
      <Box component="span" display="inline-flex">
        {children}
      </Box>
    </Tooltip>
  );
};

export const EventLink = ({eventId}: EventLinkProps) => {
  if (!eventId) {
    return <>{EMPTY_VALUE}</>;
  }

  return (
    <EventHoverCard eventId={eventId}>
      <Link
        component={RouterLink}
        to={ROUTE__EVENT_DETAILS.replace(':event_id', eventId)}
        underline="hover"
      >
        {eventId}
      </Link>
    </EventHoverCard>
  );
};
